import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Info, RotateCcw, SlidersHorizontal } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { isFreePlan } from "@/components/shared/PaidPlanRoute";
import { TableActionsMenu, type TableAction } from "@/components/shared/TableActionsMenu";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import { UploadBoletoForm } from "@/components/shared/UploadBoletoForm";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { MarkFiscalCompleteDialog } from "@/components/shared/MarkFiscalCompleteDialog";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { tenantKeys } from "@/lib/queryKeys";
import { getTenantCurrent } from "@/services/authService";
import {
  getFiscalProfile,
  getFiscalAgenda,
  requestBoleto,
  uploadBoleto,
  removeBoleto,
  markFiscalObligationCompleted,
  markFiscalObligationUncompleted,
  type FiscalAgendaObligation,
} from "@/services/fiscalService";
import { uploadTenantDocument } from "@/services/authService";
import { formatDisplayDate, parseLocalDateOnly } from "@/lib/dateFormat";
import {
  getTaxLabelKey,
  getAuthorityLabelKey,
  formatPeriodForDisplay,
  getStatusBadgeVariant,
} from "@/lib/fiscalAgendaDisplay";
import { formatDashboardCurrency } from "@/lib/currencyFormat";
import { isDeclareOnly, saldoAFavorOf } from "@/pages/fiscal-agenda/declareOnly";
import { cn } from "@/lib/utils";
import {
  FiscalAgendaMobileCard,
  FiscalAgendaPaymentDocumentSection,
} from "@/pages/fiscal-agenda/fiscal-agenda-list-blocks";
import { flattenAgenda } from "@/pages/dashboard/dashboardAlertsModel";

/** DGI tax obligations (IVA/IRAE/IRPF/IRNR) and BPS support payment document (request/upload/download). */
function supportsPaymentDocument(ob: FiscalAgendaObligation): boolean {
  return (
    (["IVA", "IRAE", "IRPF", "IRNR"].includes(ob.taxType) && ob.authority === "DGI") ||
    (["BPS", "MONOTRIBUTO"].includes(ob.taxType) && ob.authority === "BPS")
  );
}

/** True when obligation period matches current year-month (e.g. "2026-03" when today is March 2026). */
function isObligationInCurrentMonth(ob: FiscalAgendaObligation): boolean {
  const now = new Date();
  const currentYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const periodYm = ob.period.slice(0, 7);
  return periodYm === currentYm;
}

export function FiscalAgendaPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const pagination = usePaginationFromUrl();
  const [daysAhead, setDaysAhead] = useState<30 | 60 | 90>(60);
  const [taxFilter, setTaxFilter] = useState<string>("all");
  const [authorityFilter, setAuthorityFilter] = useState<string>("all");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilterCount =
    (taxFilter !== "all" ? 1 : 0) + (authorityFilter !== "all" ? 1 : 0);
  const clearAgendaFilters = (): void => {
    setTaxFilter("all");
    setAuthorityFilter("all");
  };
  const isAccountant = user?.role === "accountant";
  const [uploadBoletoObligation, setUploadBoletoObligation] =
    useState<FiscalAgendaObligation | null>(null);
  const [removeBoletoObligation, setRemoveBoletoObligation] =
    useState<FiscalAgendaObligation | null>(null);
  const [markCompleteObligation, setMarkCompleteObligation] =
    useState<FiscalAgendaObligation | null>(null);

  const year = new Date().getFullYear();
  const from = `${year}-01`;
  const to = `${year}-12`;

  const { data: currentTenant, isLoading: tenantLoading } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId),
  });
  const hasPaidPlanAccess = !isFreePlan(currentTenant?.planId);

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["fiscal-profile"] as const,
    queryFn: getFiscalProfile,
    retry: false,
    enabled: hasPaidPlanAccess,
  });

  const { data: grouped, isLoading: agendaLoading } = useQuery({
    queryKey: ["fiscal-agenda", from, to] as const,
    queryFn: () => getFiscalAgenda(from, to),
    enabled: hasPaidPlanAccess && !!profile,
  });

  const requestBoletoMutation = useMutation({
    mutationFn: (obligationId: string) => requestBoleto(obligationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-agenda"] });
      toast.success(t("estadoFiscal.boletoRequestSent"));
    },
    onError: () => {
      toast.error(t("settings.fiscalProfile.error"));
    },
  });

  const uploadBoletoMutation = useMutation({
    mutationFn: async (args: {
      obligationId: string;
      boletoNumber: string;
      file: File;
    }) => {
      const doc = await uploadTenantDocument(args.file);
      return uploadBoleto(args.obligationId, {
        boletoNumber: args.boletoNumber,
        boletoFileUrl: doc.fileUrl,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-agenda"] });
      toast.success(t("estadoFiscal.boletoUploaded"));
      setUploadBoletoObligation(null);
    },
    onError: () => {
      toast.error(t("settings.fiscalProfile.error"));
    },
  });

  const removeBoletoMutation = useMutation({
    mutationFn: (obligationId: string) => removeBoleto(obligationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-agenda"] });
      setRemoveBoletoObligation(null);
      toast.success(t("estadoFiscal.boletoRemoved"));
    },
    onError: () => {
      toast.error(t("settings.fiscalProfile.error"));
    },
  });

  const markCompleteMutation = useMutation({
    mutationFn: (args: {
      obligationId: string;
      completedDate: string;
      notes?: string;
    }) =>
      markFiscalObligationCompleted(args.obligationId, {
        completedDate: args.completedDate,
        notes: args.notes,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-agenda"] });
      queryClient.invalidateQueries({ queryKey: ["fiscal-health"] });
      toast.success(t("fiscalAgenda.markCompletedSuccess"));
      setMarkCompleteObligation(null);
    },
    onError: () => {
      toast.error(t("settings.fiscalProfile.error"));
    },
  });

  const uncompleteMutation = useMutation({
    mutationFn: (obligationId: string) =>
      markFiscalObligationUncompleted(obligationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-agenda"] });
      queryClient.invalidateQueries({ queryKey: ["fiscal-health"] });
      toast.success(t("fiscalAgenda.uncompletedSuccess"));
    },
    onError: () => {
      toast.error(t("settings.fiscalProfile.error"));
    },
  });

  const [agendaTab, setAgendaTab] = useState<"pendientes" | "completados">(
    "pendientes"
  );

  const allObligations = useMemo(() => {
    if (!grouped) return [];
    return flattenAgenda(grouped);
  }, [grouped]);

  const obligations = useMemo(() => {
    let list =
      agendaTab === "pendientes"
        ? allObligations.filter((o) => o.status !== "completed")
        : allObligations.filter((o) => o.status === "completed");
    if (taxFilter !== "all") {
      list = list.filter((o) => o.taxType === taxFilter);
    }
    if (authorityFilter !== "all") {
      list = list.filter(
        (o) => o.authority.toLowerCase() === authorityFilter
      );
    }
    if (agendaTab === "pendientes") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const end = new Date(today);
      end.setDate(end.getDate() + daysAhead);
      list = list.filter((o) => {
        // Annual obligations (IRAE/IRPF/IRNR) are visible as soon as their filing window opens
        if (o.filingStartDate && parseLocalDateOnly(o.filingStartDate) <= today) return true;
        return parseLocalDateOnly(o.dueDate) <= end;
      });
    }
    return [...list].sort(
      (a, b) =>
        parseLocalDateOnly(a.dueDate).getTime() -
        parseLocalDateOnly(b.dueDate).getTime()
    );
  }, [allObligations, agendaTab, taxFilter, authorityFilter, daysAhead]);

  useEffect(() => {
    pagination.setTotal(obligations.length);
  }, [obligations.length, pagination]);

  useEffect(() => {
    const totalPages = Math.ceil(obligations.length / pagination.limit) || 1;
    if (pagination.page > totalPages && totalPages >= 1) {
      pagination.goToPage(1);
    }
  }, [obligations.length, pagination.limit, pagination.page, pagination.goToPage]);

  const paginatedObligations = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit;
    return obligations.slice(start, start + pagination.limit);
  }, [obligations, pagination.page, pagination.limit]);

  const needsBpsBanner = Boolean(
    profile &&
      profile.taxes?.includes("BPS") &&
      !profile.bpsCompanyNumber?.trim()
  );

  if (tenantLoading) {
    return <LoadingPage />;
  }

  if (!hasPaidPlanAccess) {
    return (
      <div className="space-y-4 fx-content">
        <PageHeader
          title={t("fiscalAgenda.title")}
          description={t("fiscalAgenda.subtitle")}
        />
        <div
          className="min-h-[32vh] rounded-lg border border-dashed border-border/60 bg-muted/20"
          aria-hidden
        />
      </div>
    );
  }

  if (hasPaidPlanAccess && !profile && !profileLoading && !agendaLoading) {
    return (
      <div className="space-y-4 fx-content">
        <PageHeader
          title={t("fiscalAgenda.title")}
          description={t("fiscalAgenda.subtitle")}
        />
        <TableCard>
          <div className="min-h-[40vh] flex items-center justify-center">
            <EmptyState
              title={t("fiscalAgenda.empty")}
              message={t("settings.fiscalProfile.noProfile")}
              className="py-12"
            />
          </div>
        </TableCard>
      </div>
    );
  }

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            {t("fiscalAgenda.title")}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:ring-2"
                  aria-label={t("fiscalAgenda.tooltipDisclaimer")}
                >
                  <Info className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                {t("fiscalAgenda.tooltipDisclaimer")}
              </TooltipContent>
            </Tooltip>
          </span>
        }
        description={t("fiscalAgenda.subtitle")}
      />

      {needsBpsBanner && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
        >
          <Info className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <span>{t("fiscalAgenda.needsBpsBanner")}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Label
            htmlFor="fiscal-agenda-time-range"
            className="text-muted-foreground shrink-0 text-sm font-normal"
          >
            {t("fiscalAgenda.filterTimeRange")}
          </Label>
          <Select
            value={String(daysAhead)}
            onValueChange={(v) => setDaysAhead(Number(v) as 30 | 60 | 90)}
          >
            <SelectTrigger
              id="fiscal-agenda-time-range"
              className="w-[160px] transition-colors duration-150"
            >
              <SelectValue placeholder={t("fiscalAgenda.filterTimeRange")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30">{t("fiscalAgenda.days30")}</SelectItem>
              <SelectItem value="60">{t("fiscalAgenda.days60")}</SelectItem>
              <SelectItem value="90">{t("fiscalAgenda.days90")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Dialog open={filtersOpen} onOpenChange={setFiltersOpen}>
          <DialogTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-10 gap-2 transition-colors duration-150"
            >
              <SlidersHorizontal className="h-4 w-4 shrink-0" aria-hidden />
              <span className="truncate">
                {t("fiscalAgenda.filters", "Filters")}
              </span>
              {activeFilterCount > 0 ? (
                <Badge variant="secondary" className="shrink-0 tabular-nums">
                  {activeFilterCount}
                </Badge>
              ) : null}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[min(90vh,720px)] w-[calc(100vw-1.5rem)] max-w-lg gap-0 overflow-hidden p-0 sm:rounded-xl">
            <DialogHeader className="border-b border-border px-4 py-4 text-left">
              <DialogTitle className="text-foreground">
                {t("fiscalAgenda.filtersSheetTitle", "Filter agenda")}
              </DialogTitle>
            </DialogHeader>
            <div className="flex max-h-[min(70vh,560px)] flex-col gap-5 overflow-y-auto overscroll-contain px-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="fiscal-agenda-tax-type">
                  {t("fiscalAgenda.filterTaxType")}
                </Label>
                <Select value={taxFilter} onValueChange={setTaxFilter}>
                  <SelectTrigger
                    id="fiscal-agenda-tax-type"
                    className="w-full"
                  >
                    <SelectValue
                      placeholder={t("fiscalAgenda.filterTaxType")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("fiscalAgenda.filterAll")}
                    </SelectItem>
                    <SelectItem value="IVA">
                      {t("fiscalAgenda.tax.iva")}
                    </SelectItem>
                    <SelectItem value="IRAE">
                      {t("fiscalAgenda.tax.irae")}
                    </SelectItem>
                    <SelectItem value="IRPF">
                      {t("fiscalAgenda.tax.irpf")}
                    </SelectItem>
                    <SelectItem value="IRNR">
                      {t("fiscalAgenda.tax.irnr")}
                    </SelectItem>
                    <SelectItem value="BPS">
                      {t("fiscalAgenda.tax.bps")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="fiscal-agenda-authority">
                  {t("fiscalAgenda.filterAuthority")}
                </Label>
                <Select
                  value={authorityFilter}
                  onValueChange={setAuthorityFilter}
                >
                  <SelectTrigger
                    id="fiscal-agenda-authority"
                    className="w-full"
                  >
                    <SelectValue
                      placeholder={t("fiscalAgenda.filterAuthority")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("fiscalAgenda.filterAll")}
                    </SelectItem>
                    <SelectItem value="dgi">
                      {t("fiscalAgenda.authority.dgi")}
                    </SelectItem>
                    <SelectItem value="bps">
                      {t("fiscalAgenda.authority.bps")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div
              className={cn(
                "flex flex-col gap-2 border-t border-border bg-muted/30 px-4 py-3 sm:flex-row sm:items-center",
                activeFilterCount > 0 ? "sm:justify-between" : "sm:justify-end"
              )}
            >
              {activeFilterCount > 0 ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full transition-colors duration-150 sm:w-auto"
                  onClick={clearAgendaFilters}
                >
                  {t("fiscalAgenda.clearFilters", "Clear filters")}
                </Button>
              ) : null}
              <Button
                type="button"
                className="w-full transition-colors duration-150 sm:w-auto"
                onClick={() => setFiltersOpen(false)}
              >
                {t("fiscalAgenda.filtersDone", "Done")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {activeFilterCount > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground transition-colors duration-150"
            onClick={clearAgendaFilters}
          >
            {t("fiscalAgenda.clearFilters", "Clear filters")}
          </Button>
        ) : null}
      </div>

      <Tabs
        value={agendaTab}
        onValueChange={(v) =>
          setAgendaTab(v === "completados" ? "completados" : "pendientes")
        }
      >
        <TabsList className="mb-2">
          <TabsTrigger value="pendientes">
            {t("fiscalAgenda.tabPendientes")}
          </TabsTrigger>
          <TabsTrigger value="completados">
            {t("fiscalAgenda.tabCompletados")}
          </TabsTrigger>
        </TabsList>

      <TableCard>
        {(profileLoading || agendaLoading) ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : obligations.length === 0 ? (
          <div className="min-h-[40vh] flex items-center justify-center">
            <EmptyState
              title={t("fiscalAgenda.empty")}
              className="py-12"
            />
          </div>
        ) : (
          <>
            <div className="space-y-3 py-2 md:hidden">
              {paginatedObligations.map((ob) => (
                <FiscalAgendaMobileCard
                  key={ob.id}
                  obligation={ob}
                  periodLabel={formatPeriodForDisplay(ob.period, i18n.language)}
                  locale={i18n.language}
                  showNotes={agendaTab === "completados"}
                  agendaTab={agendaTab}
                  isAccountant={isAccountant}
                  supportsPaymentDocument={supportsPaymentDocument(ob)}
                  isInCurrentMonth={isObligationInCurrentMonth(ob)}
                  onRequestBoleto={() => requestBoletoMutation.mutate(ob.id)}
                  isRequestingBoleto={
                    requestBoletoMutation.isPending &&
                    requestBoletoMutation.variables === ob.id
                  }
                  onOpenUpload={() => setUploadBoletoObligation(ob)}
                  onOpenRemove={() => setRemoveBoletoObligation(ob)}
                  onOpenMarkComplete={() => setMarkCompleteObligation(ob)}
                  onUncomplete={
                    agendaTab === "completados"
                      ? () => uncompleteMutation.mutate(ob.id)
                      : undefined
                  }
                  isUncompleting={
                    uncompleteMutation.isPending &&
                    uncompleteMutation.variables === ob.id
                  }
                />
              ))}
            </div>
            <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("fiscalAgenda.columnAuthority")}</TableHead>
                  <TableHead>{t("fiscalAgenda.columnTax")}</TableHead>
                  <TableHead>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help underline decoration-dotted decoration-muted-foreground">
                          {t("fiscalAgenda.columnPeriod")}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[240px]">
                        <p>{t("fiscalAgenda.columnPeriodTooltip")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TableHead>
                  <TableHead>{t("fiscalAgenda.columnDueDate")}</TableHead>
                  <TableHead>{t("fiscalAgenda.columnStatus")}</TableHead>
                  <TableHead className="w-[200px]">
                    {t("fiscalAgenda.columnPaymentDocument")}
                  </TableHead>
                  {agendaTab === "completados" && (
                    <>
                      <TableHead>
                        {t("fiscalAgenda.columnPaymentDate")}
                      </TableHead>
                      <TableHead className="max-w-[200px]">
                        {t("fiscalAgenda.columnNotes")}
                      </TableHead>
                      <TableHead className="w-[100px]">
                        {t("fiscalAgenda.columnActions")}
                      </TableHead>
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedObligations.map((ob) => (
                <FiscalAgendaRow
                  key={ob.id}
                  obligation={ob}
                  periodLabel={formatPeriodForDisplay(ob.period, i18n.language)}
                  locale={i18n.language}
                  showNotes={agendaTab === "completados"}
                  agendaTab={agendaTab}
                  isAccountant={isAccountant}
                  supportsPaymentDocument={supportsPaymentDocument(ob)}
                  isInCurrentMonth={isObligationInCurrentMonth(ob)}
                  onRequestBoleto={() => requestBoletoMutation.mutate(ob.id)}
                  isRequestingBoleto={
                    requestBoletoMutation.isPending &&
                    requestBoletoMutation.variables === ob.id
                  }
                  onOpenUpload={() => setUploadBoletoObligation(ob)}
                  onOpenRemove={() => setRemoveBoletoObligation(ob)}
                  onOpenMarkComplete={() => setMarkCompleteObligation(ob)}
                  onUncomplete={
                    agendaTab === "completados"
                      ? () => uncompleteMutation.mutate(ob.id)
                      : undefined
                  }
                  isUncompleting={
                    uncompleteMutation.isPending &&
                    uncompleteMutation.variables === ob.id
                  }
                />
                ))}
              </TableBody>
            </Table>
            </div>
            <TablePagination
            pagination={pagination}
            rowsPerPageLabel={t("fiscalAgenda.rowsPerPage", "Rows per page")}
            pageOfLabel={t("fiscalAgenda.pageOf", "{{from}}-{{to}} of {{total}}", {
              from: (pagination.page - 1) * pagination.limit + 1,
              to: Math.min(pagination.page * pagination.limit, pagination.total),
              total: pagination.total,
            })}
            onRefresh={() => queryClient.invalidateQueries({ queryKey: ["fiscal-agenda"] })}
            isRefreshing={agendaLoading ?? false}
          />
          </>
        )}
      </TableCard>
      </Tabs>

      <Dialog
        open={uploadBoletoObligation !== null}
        onOpenChange={(open) => !open && setUploadBoletoObligation(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("estadoFiscal.uploadBoletoDialogTitle")}</DialogTitle>
          </DialogHeader>
          {uploadBoletoObligation && (
            <UploadBoletoForm
              obligationId={uploadBoletoObligation.id}
              onSuccess={() => setUploadBoletoObligation(null)}
              onCancel={() => setUploadBoletoObligation(null)}
              uploadBoletoMutation={uploadBoletoMutation}
              defaultBoletoNumber={uploadBoletoObligation.boletoNumber ?? ""}
            />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={removeBoletoObligation !== null}
        onOpenChange={(open) => !open && setRemoveBoletoObligation(null)}
        title={t("estadoFiscal.ivaRemoveBoletoConfirmTitle")}
        description={t("estadoFiscal.ivaRemoveBoletoConfirmDescription")}
        confirmText={t("estadoFiscal.ivaRemoveBoleto")}
        variant="destructive"
        onConfirm={() => {
          if (removeBoletoObligation) {
            removeBoletoMutation.mutate(removeBoletoObligation.id);
          }
        }}
        isLoading={removeBoletoMutation.isPending}
      />

      <MarkFiscalCompleteDialog
        obligation={markCompleteObligation}
        onClose={() => setMarkCompleteObligation(null)}
        onConfirm={(completedDate, notes) => {
          if (markCompleteObligation) {
            markCompleteMutation.mutate({
              obligationId: markCompleteObligation.id,
              completedDate,
              notes: notes || undefined,
            });
          }
        }}
        isSubmitting={markCompleteMutation.isPending}
      />

      <p className="text-xs text-muted-foreground">
        {t("fiscalAgenda.sourceDisclaimer")}
      </p>
    </div>
  );
}

function FiscalAgendaRow({
  obligation,
  periodLabel,
  locale,
  showNotes,
  agendaTab,
  isAccountant,
  supportsPaymentDocument,
  isInCurrentMonth,
  onRequestBoleto,
  isRequestingBoleto,
  onOpenUpload,
  onOpenRemove,
  onOpenMarkComplete,
  onUncomplete,
  isUncompleting,
}: {
  obligation: FiscalAgendaObligation;
  periodLabel: string;
  locale?: string;
  showNotes: boolean;
  agendaTab: "pendientes" | "completados";
  isAccountant: boolean;
  supportsPaymentDocument: boolean;
  isInCurrentMonth: boolean;
  onRequestBoleto: () => void;
  isRequestingBoleto: boolean;
  onOpenUpload: () => void;
  onOpenRemove: () => void;
  onOpenMarkComplete?: () => void;
  onUncomplete?: () => void;
  isUncompleting?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const statusKey = obligation.status;
  // Nothing to pay: still due to declare, so keep the row but drop the
  // unpaid-debt framing (see declareOnly.ts).
  const declareOnly = isDeclareOnly(obligation);
  const saldoAFavor = saldoAFavorOf(obligation);
  const isDueToday = obligation.status === "due_today" && !declareOnly;
  const isOverdue = obligation.status === "overdue" && !declareOnly;
  return (
    <TableRow
      className={cn(
        isOverdue &&
          "bg-red-50 dark:bg-red-950/30 border-l-4 border-l-red-500",
        isDueToday &&
          "bg-amber-50 dark:bg-amber-950/30 border-l-4 border-l-amber-500"
      )}
    >
      <TableCell className="font-medium">
        {t(getAuthorityLabelKey(obligation.authority))}
      </TableCell>
      <TableCell className="font-medium">
        {t(getTaxLabelKey(obligation.taxType))}
      </TableCell>
      <TableCell>{periodLabel}</TableCell>
      <TableCell>
        {formatDisplayDate(obligation.dueDate, {
          useCalendarDate: true,
          locale,
        })}
        {obligation.internetPaymentDueDate ? (
          <span className="mt-0.5 block text-xs text-muted-foreground">
            {t("fiscalAgenda.internetPaymentUntil", "Por internet, hasta el {{date}}", {
              date: formatDisplayDate(obligation.internetPaymentDueDate, {
                useCalendarDate: true,
                locale,
              }),
            })}
          </span>
        ) : null}
      </TableCell>
      <TableCell>
        <StatusBadge
          variant={declareOnly ? "neutral" : getStatusBadgeVariant(obligation.status)}
          className={cn(
            "inline-flex text-xs font-medium",
            (isDueToday || isOverdue) && "font-semibold"
          )}
        >
          {declareOnly
            ? t("fiscalAgenda.status.declareOnly", "A declarar — sin importe a pagar")
            : t(`fiscalAgenda.status.${statusKey}`)}
        </StatusBadge>
        {declareOnly && saldoAFavor > 0 ? (
          <p className="mt-1 text-xs text-muted-foreground">
            {t("fiscalAgenda.saldoAFavor", "Saldo a favor")}: $U{" "}
            {formatDashboardCurrency(saldoAFavor)}
          </p>
        ) : null}
      </TableCell>
      <TableCell className="align-middle text-center">
        <FiscalAgendaPaymentDocumentSection
          obligation={obligation}
          agendaTab={agendaTab}
          isAccountant={isAccountant}
          supportsPaymentDocument={supportsPaymentDocument}
          isInCurrentMonth={isInCurrentMonth}
          onRequestBoleto={onRequestBoleto}
          isRequestingBoleto={isRequestingBoleto}
          onOpenUpload={onOpenUpload}
          onOpenRemove={onOpenRemove}
          onOpenMarkComplete={onOpenMarkComplete}
        />
      </TableCell>
      {showNotes && (
        <>
          <TableCell>
            {obligation.completedAt
              ? formatDisplayDate(obligation.completedAt, {
                  useCalendarDate: true,
                  locale,
                })
              : "—"}
          </TableCell>
          <TableCell className="max-w-[200px] text-muted-foreground text-sm">
            {obligation.notes ?? "—"}
          </TableCell>
          <TableCell className="align-middle">
            {onUncomplete ? (
              <TableActionsMenu
                actions={[
                  {
                    label: t("fiscalAgenda.uncomplete"),
                    icon: RotateCcw,
                    onClick: onUncomplete,
                  },
                ]}
                onTriggerClick={(e) => e.stopPropagation()}
                triggerLabel={t("fiscalAgenda.actions")}
                disabled={isUncompleting}
              />
            ) : null}
          </TableCell>
        </>
      )}
    </TableRow>
  );
}
